import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { CollectionItem, Kitchen } from "@prmf/site-data";
import { routes } from "@prmf/site-router";
import { NavLink, useLoaderData, useParams } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { twJoin } from "tailwind-merge";
import BgImage from "./BgImage";
import PriceGroupMeter from "./PriceGroupMeter";

function unique(arr: any[]) {
  return arr.reduce<string[]>((p, c) => {
    if (c && !p.includes(c)) p.push(c);
    return p;
  }, []);
}

function getSpecsSummary(doors: CollectionItem[]) {
  const hitSpecs = doors.map((d) => d.specs);
  const specKeys = Object.keys(hitSpecs[0] || {});
  return specKeys.reduce<Record<string, string[]>>((p, c) => {
    p[c] = unique(hitSpecs.map((s) => s?.[c as "range"]));
    return p;
  }, {});
}

function DoorSample(props: { data: CollectionItem }) {
  const { refs, floatingStyles } = useFloating({
    placement: "bottom",
    middleware: [offset(5), flip(), shift({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  });
  const d = props.data;
  return (
    <div
      ref={refs.setReference}
      className={twJoin(
        "group relative",
        "flex items-center justify-center",
        "flex-1 basis-10",
        "cursor-default",
      )}
    >
      <BgImage
        src={d.thumbnail}
        className="aspect-square !w-10 rounded-full shadow"
      >
        <div
          className={twJoin(
            "absolute inset-1",
            "place-items-center",
            "rounded-full bg-white/90 text-xs font-bold",
            "hidden group-hover:grid",
          )}
        >
          {d.range}
        </div>
      </BgImage>
      <div
        className="absolute z-10 hidden group-hover:block"
        ref={refs.setFloating}
        style={floatingStyles}
      >
        <h5
          className={twJoin(
            "whitespace-nowrap",
            "rounded bg-black/70 text-white",
            "p-2 text-xs",
          )}
        >
          {d.name}
        </h5>
      </div>
    </div>
  );
}

export function Column(props: { defaultValue?: string }) {
  const { kitchens } = useLoaderData<{ kitchens: Kitchen[] }>();
  const [id, setId] = useState(props.defaultValue);
  const kitchen = kitchens?.find((k) => k.slug === id);
  const mSpecs = getSpecsSummary(kitchen?.parts.doors || []);

  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (selectRef.current) selectRef.current.value = "";
  }, [selectRef]);

  return (
    <div
      className={twJoin(
        "w-[320px]",
        "snap-center snap-always",
        "flex flex-col",
        "rounded-lg bg-white text-center shadow-xl",
        !kitchen && "mb-auto",
      )}
    >
      <div className="relative space-y-4 p-6">
        <select
          ref={selectRef}
          value={id}
          className="w-full rounded"
          onChange={(e) => {
            setId(e.currentTarget.value);
          }}
        >
          {!id && <option value="">Please Select</option>}
          {kitchens?.map((k) => (
            <option key={k.slug} value={k.slug}>
              {k.name}
            </option>
          ))}
        </select>
        {kitchen && (
          <NavLink
            to={`${routes.kitchens}/${kitchen.slug}`}
            className={twJoin(
              "mx-auto table rounded-full bg-gray-500",
              "px-4 py-2 text-xs font-medium text-white",
            )}
          >
            Learn more
          </NavLink>
        )}
      </div>

      {kitchen && (
        <>
          <BgImage
            src={`/media/${kitchen.slug}/_thumb-main.jpg`}
            className="aspect-[4/2.5]"
          />
          <div className="p-6">
            <h4 className="text-sm font-bold">Price Group</h4>
            <h4 className="text-2xl font-bold">{kitchen.priceGroup}</h4>
            {kitchen.priceGroup ? (
              <PriceGroupMeter
                className="my-2 justify-center"
                color={kitchen.accentColor}
                group={kitchen.priceGroup}
              />
            ) : (
              "N/A"
            )}
          </div>
          <div className="flex-1 space-y-4 bg-gray-50 px-4 py-6">
            <h4 className="text-sm font-bold">Door options</h4>
            <div className="flex w-[300px] flex-wrap justify-center gap-5">
              {kitchen.parts.doors.map((d, idx) => (
                <DoorSample key={idx} data={d} />
              ))}
            </div>
          </div>
          <div className="grid divide-y">
            {["edging", "material", "thickness", "shakerThickness"].map(
              (k) =>
                mSpecs[k] && (
                  <div key={k} className="py-4">
                    <h4 className="text font-bold capitalize">{k}</h4>
                    <span className="text-sm font-light">
                      {mSpecs?.[k]?.join(" / ")}
                    </span>
                  </div>
                ),
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function KitchenCompare() {
  const slug = useParams().range;
  const [expanded, setExpanded] = useState(false);
  const colStyle = "!w-fit";
  return (
    <div className="bg-gray-50">
      <section className="py-20">
        <h3 className="mb-4 text-center text-xl font-bold">
          Compare our ranges
          {expanded ? (
            <small
              onClick={() => setExpanded(false)}
              className="mt-4 block text-sm underline"
            >
              Remove column
            </small>
          ) : (
            <small
              onClick={() => setExpanded(true)}
              className="mt-4 block text-sm underline"
            >
              Add column
            </small>
          )}
        </h3>
        <div className="flex justify-center">
          <Swiper
            //centeredSlides
            spaceBetween={30}
            slidesPerView="auto"
            className="mx-auto !py-10"
          >
            <SwiperSlide
              className={colStyle}
              children={<Column defaultValue={slug} />}
            />
            <SwiperSlide className={colStyle} children={<Column />} />
            {expanded && (
              <SwiperSlide className={colStyle} children={<Column />} />
            )}
          </Swiper>
        </div>
      </section>
    </div>
  );
}
